import api from '../services/api';
import React,{useState} from 'react';
import Layout from '../components/layout/Layout';
import {Input,Select} from '../components/common/FormField';
import Spinner from '../components/common/Spinner';

const BASE = process.env.REACT_APP_API_URL;

async function fhirFetch(path:string){
  const r=await fetch(`${BASE}${path}`,{headers:{Authorization:`Bearer ${localStorage.getItem('accessToken')}`}});
  if(!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

export default function FHIRPage(){
  const [tab,setTab]=useState<'explorer'|'resources'|'bundle'|'capability'>('resources');
  const [resource,setResource]=useState('Patient');
  const [resourceId,setResourceId]=useState('');
  const [result,setResult]=useState<any>(null);
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState('');
  const [searchParam,setSearchParam]=useState('identifier');
  const [searchVal,setSearchVal]=useState('');

  const fetchResource=async()=>{
    if(!resourceId){setError('Enter a resource ID');return;}
    setLoading(true);setError('');setResult(null);
    try{
      const path = resource==='Patient'&&resourceId.includes('$everything')
        ?`/fhir/r4/Patient/${resourceId.replace('/$everything','').trim()}/$everything`
        :`/fhir/r4/${resource}/${resourceId}`;
      const data=await fhirFetch(path);
      setResult(data);
    }catch(e:any){setError(e.message||'Error fetching resource');}
    finally{setLoading(false);}
  };

  const searchResource=async()=>{
    setLoading(true);setError('');setResult(null);
    try{
      const data=await fhirFetch(`/fhir/r4/${resource}?${searchParam}=${encodeURIComponent(searchVal)}`);
      setResult(data);
    }catch(e:any){setError(e.message||'Search failed');}
    finally{setLoading(false);}
  };

  const fetchCapability=async()=>{
    setLoading(true);setError('');setResult(null);
    try{const data=await fhirFetch('/fhir/r4/metadata');setResult(data);}
    catch(e:any){setError(e.message);}
    finally{setLoading(false);}
  };

  const RESOURCES=['Patient','Observation','Condition','MedicationRequest','DiagnosticReport','Encounter','Bundle'];
  const SEARCH_PARAMS:Record<string,string[]>={Patient:['identifier','name','birthdate'],Observation:['patient','code'],Condition:['patient','code'],Encounter:['patient','status'],MedicationRequest:['patient','status'],DiagnosticReport:['patient','status']};

  const FHIR_RESOURCES_INFO=[
    {name:'Patient',icon:'👤',desc:'Patient demographic information',loinc:'—',snomed:'—',example:'GET /fhir/r4/Patient/{id}'},
    {name:'Observation',icon:'🔬',desc:'Lab results, vitals using LOINC codes',loinc:'Yes',snomed:'—',example:'GET /fhir/r4/Observation/{id}'},
    {name:'Condition',icon:'🏥',desc:'Diagnoses using ICD-11 & SNOMED CT',loinc:'—',snomed:'Yes',example:'GET /fhir/r4/Condition/{id}'},
    {name:'MedicationRequest',icon:'💊',desc:'Prescriptions with RxNorm/SNOMED',loinc:'—',snomed:'Yes',example:'GET /fhir/r4/MedicationRequest/{id}'},
    {name:'DiagnosticReport',icon:'📊',desc:'Lab reports with LOINC codes',loinc:'Yes',snomed:'—',example:'GET /fhir/r4/DiagnosticReport/{id}'},
    {name:'Encounter',icon:'📅',desc:'Patient visits & appointments',loinc:'—',snomed:'Yes',example:'GET /fhir/r4/Encounter/{id}'},
    {name:'Bundle',icon:'📦',desc:'Collection of FHIR resources',loinc:'—',snomed:'—',example:'GET /fhir/r4/Patient/{id}/$everything'},
  ];

  return(
    <Layout title="FHIR R4 Interoperability">
      <div className="flex gap-2 mb-5">
        {[['resources','📚 Resources'],['explorer','🔍 Explorer'],['bundle','📦 Patient Bundle'],['capability','🔧 Capability']].map(([t,l])=>(
          <button key={t} onClick={()=>{setTab(t as any);setResult(null);setError('');}}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab===t?'bg-[#02357A] text-white shadow':'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
        ))}
      </div>

      {tab==='resources'&&(
        <div className="space-y-5">
          <div className="card p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center text-xl">🔥</div>
              <div><h3 className="font-bold text-slate-800">HL7 FHIR R4 (Fast Healthcare Interoperability Resources)</h3><p className="text-slate-500 text-xs">Version 4.0.1 | Kenya HMIS | SHA-ready</p></div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm mb-4">
              {[['Base URL',`${BASE}/fhir/r4`,'font-mono text-xs'],['Version','4.0.1',''],['Format','JSON',''],['Auth','JWT Bearer','']].map(([l,v,cls])=>(
                <div key={l} className="bg-slate-50 p-3 rounded-lg"><p className="text-slate-400 text-xs">{l}</p><p className={`font-semibold text-slate-800 mt-0.5 ${cls}`}>{v}</p></div>
              ))}
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm">
              <p className="font-semibold text-[#02357A] mb-2">Kenya HMIS interoperability</p>
              <p className="text-[#02357A] text-xs">This FHIR R4 server exposes Patient, Encounter, Observation, and related resources for hospital interoperability. Terminology uses ICD-10, SNOMED CT, and LOINC. SHA claims use the DHA middleware, not this FHIR endpoint.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {FHIR_RESOURCES_INFO.map(r=>(
              <div key={r.name} className="card p-4 hover:shadow-md transition-all border-t-4 border-blue-400">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{r.icon}</span>
                  <h4 className="font-bold text-slate-800">{r.name}</h4>
                </div>
                <p className="text-slate-500 text-xs mb-3">{r.desc}</p>
                <div className="flex gap-1 mb-3">
                  {r.loinc==='Yes'&&<span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-medium">LOINC</span>}
                  {r.snomed==='Yes'&&<span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">SNOMED</span>}
                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded font-medium">FHIR R4</span>
                </div>
                <code className="text-xs bg-slate-100 text-slate-600 px-2 py-1.5 rounded block font-mono">{r.example}</code>
                <button onClick={()=>{setTab('explorer');setResource(r.name);setResult(null);}} className="btn-secondary text-xs py-1 px-3 mt-3 w-full">Try it →</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab==='explorer'&&(
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="space-y-4">
            <div className="card p-5">
              <h3 className="font-semibold text-slate-700 mb-4">🔍 FHIR Resource Explorer</h3>
              <div className="space-y-3">
                <div>
                  <label className="label">Resource Type</label>
                  <Select value={resource} onChange={e=>setResource(e.target.value)}>
                    {RESOURCES.map(r=><option key={r}>{r}</option>)}
                  </Select>
                </div>
                <div>
                  <label className="label">Resource ID</label>
                  <Input value={resourceId} onChange={e=>setResourceId(e.target.value)} placeholder={`Enter ${resource} ID (UUID or UHID)`}/>
                </div>
                <button onClick={fetchResource} disabled={loading} className="btn-primary w-full">
                  {loading?'Fetching...':'GET /fhir/r4/'+resource+'/{id}'}
                </button>
              </div>
            </div>

            <div className="card p-5">
              <h3 className="font-semibold text-slate-700 mb-4">🔎 FHIR Search</h3>
              <div className="space-y-3">
                <div>
                  <label className="label">Search Parameter</label>
                  <Select value={searchParam} onChange={e=>setSearchParam(e.target.value)}>
                    {(SEARCH_PARAMS[resource]||['identifier']).map(p=><option key={p}>{p}</option>)}
                  </Select>
                </div>
                <div>
                  <label className="label">Search Value</label>
                  <Input value={searchVal} onChange={e=>setSearchVal(e.target.value)} placeholder="Search value..."/>
                </div>
                <button onClick={searchResource} disabled={loading} className="btn-secondary w-full">
                  {loading?'Searching...':'GET /fhir/r4/'+resource+'?'+searchParam+'=...'}
                </button>
              </div>
            </div>

            <div className="card p-5">
              <h3 className="font-semibold text-slate-700 mb-3">📋 Common Operations</h3>
              <div className="space-y-2">
                {[
                  {label:'GET Patient by ID',path:'/fhir/r4/Patient/{id}'},
                  {label:'GET Patient Everything',path:'/fhir/r4/Patient/{id}/$everything'},
                  {label:'Search Patient by name',path:'/fhir/r4/Patient?name=John'},
                  {label:'GET Lab Report',path:'/fhir/r4/DiagnosticReport/{id}'},
                  {label:'GET Vitals',path:'/fhir/r4/Observation/{id}'},
                  {label:'GET Capability',path:'/fhir/r4/metadata'},
                ].map(op=>(
                  <div key={op.label} className="bg-slate-50 p-2.5 rounded-lg">
                    <p className="text-xs font-medium text-slate-600 mb-0.5">{op.label}</p>
                    <code className="text-xs font-mono text-[#02357A]">{BASE}{op.path}</code>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            {loading&&<div className="card p-8 text-center"><Spinner/><p className="text-slate-500 text-sm mt-2">Fetching FHIR resource...</p></div>}
            {error&&<div className="card p-5 border-red-200 bg-red-50"><p className="text-red-600 font-semibold text-sm mb-1">❌ Error</p><p className="text-red-500 text-sm">{error}</p></div>}
            {result&&!loading&&(
              <div className="card overflow-hidden">
                <div className="p-4 border-b bg-slate-50 flex justify-between items-center">
                  <div>
                    <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded">200 OK</span>
                    <span className="text-xs text-slate-500 ml-2">resourceType: {result.resourceType}</span>
                  </div>
                  <button onClick={()=>navigator.clipboard.writeText(JSON.stringify(result,null,2))} className="btn-secondary text-xs py-1 px-3">📋 Copy JSON</button>
                </div>
                <div className="p-4 max-h-[600px] overflow-y-auto">
                  <pre className="text-xs font-mono text-slate-700 whitespace-pre-wrap">{JSON.stringify(result,null,2)}</pre>
                </div>
              </div>
            )}
            {!result&&!loading&&!error&&(
              <div className="card p-8 text-center">
                <span className="text-5xl block mb-3">🔥</span>
                <p className="font-semibold text-slate-700">FHIR R4 Explorer</p>
                <p className="text-slate-500 text-sm mt-1">Select a resource type and enter an ID to view the FHIR resource</p>
              </div>
            )}
          </div>
        </div>
      )}

      {tab==='bundle'&&(
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="card p-5">
            <h3 className="font-semibold text-slate-700 mb-4">📦 Patient Summary Bundle ($everything)</h3>
            <p className="text-slate-500 text-sm mb-4">Generate a complete FHIR R4 Bundle containing all patient health information — encounters, vitals, diagnoses, prescriptions, and lab reports.</p>
            <div className="space-y-3 mb-4">
              <div><label className="label">Patient ID (UUID)</label><Input value={resourceId} onChange={e=>setResourceId(e.target.value)} placeholder="Patient database UUID"/></div>
            </div>
            <button onClick={()=>{setResource('Patient');fetchResource();}} disabled={loading||!resourceId} className="btn-primary w-full mb-2">
              {loading?'Generating Bundle...':'📦 Generate Patient Bundle'}
            </button>
            <button onClick={()=>{setResourceId('$everything');fetchResource();}} className="btn-secondary w-full text-sm">Try with Sample Data</button>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg text-xs text-[#02357A]">
              <p className="font-semibold mb-1">Bundle Contents:</p>
              <ul className="space-y-0.5 list-disc list-inside">
                {['Patient demographics','Encounters (appointments)','Vital signs (Observations)','Conditions (diagnoses with ICD-11)','Medication Requests (prescriptions)','Diagnostic Reports (lab results)'].map(i=><li key={i}>{i}</li>)}
              </ul>
            </div>
          </div>
          <div>
            {loading&&<div className="card p-8 text-center"><Spinner/><p className="text-slate-500 text-sm mt-2">Building FHIR Bundle...</p></div>}
            {result&&!loading&&(
              <div className="card overflow-hidden">
                <div className="p-4 border-b bg-green-50 flex justify-between items-center">
                  <div>
                    <span className="text-xs font-bold text-green-700">✅ Bundle Generated</span>
                    <span className="text-xs text-slate-500 ml-2">{result.entry?.length||0} resources</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={()=>navigator.clipboard.writeText(JSON.stringify(result,null,2))} className="btn-secondary text-xs py-1 px-3">📋 Copy</button>
                    <button onClick={()=>{const b=new Blob([JSON.stringify(result,null,2)],{type:'application/json'});const u=URL.createObjectURL(b);const a=document.createElement('a');a.href=u;a.download='patient-fhir-bundle.json';a.click();}} className="btn-primary text-xs py-1 px-3">⬇️ Download</button>
                  </div>
                </div>
                {result.entry&&(
                  <div className="p-4 border-b bg-slate-50">
                    <p className="text-xs font-semibold text-slate-600 mb-2">Bundle Summary:</p>
                    <div className="flex flex-wrap gap-1">
                      {Array.from(new Set(result.entry.map((e:any)=>e.resource?.resourceType))).map((rt:any)=>{
                        const count=result.entry.filter((e:any)=>e.resource?.resourceType===rt).length;
                        return<span key={rt} className="text-xs bg-blue-100 text-[#02357A] px-2 py-0.5 rounded font-medium">{rt} ({count})</span>;
                      })}
                    </div>
                  </div>
                )}
                <div className="p-4 max-h-[400px] overflow-y-auto">
                  <pre className="text-xs font-mono text-slate-700 whitespace-pre-wrap">{JSON.stringify(result,null,2)}</pre>
                </div>
              </div>
            )}
            {!result&&!loading&&<div className="card p-8 text-center"><span className="text-5xl block mb-3">📦</span><p className="text-slate-500">Enter a patient ID to generate FHIR bundle</p></div>}
          </div>
        </div>
      )}

      {tab==='capability'&&(
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="card p-5">
            <h3 className="font-semibold text-slate-700 mb-4">🔧 Capability Statement</h3>
            <p className="text-slate-500 text-sm mb-4">The FHIR Capability Statement describes what this server can do — supported resources, operations, search parameters, and security.</p>
            <button onClick={fetchCapability} disabled={loading} className="btn-primary w-full mb-4">
              {loading?'Loading...':'GET /fhir/r4/metadata'}
            </button>
            <div className="space-y-2">
              {[['FHIR Version','4.0.1'],['Format','JSON'],['Security','JWT Bearer Token'],['Base URL',`${BASE}/fhir/r4`],['Profiles','HL7 FHIR R4'],['Encoding','UTF-8']].map(([l,v])=>(
                <div key={l} className="flex justify-between text-sm py-2 border-b border-slate-100">
                  <span className="text-slate-500">{l}</span><span className="font-medium text-slate-700 text-xs font-mono">{v}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            {loading&&<div className="card p-8 text-center"><Spinner/></div>}
            {result&&!loading&&(
              <div className="card overflow-hidden">
                <div className="p-4 border-b bg-slate-50 flex justify-between">
                  <span className="text-sm font-semibold text-slate-700">CapabilityStatement</span>
                  <button onClick={()=>navigator.clipboard.writeText(JSON.stringify(result,null,2))} className="btn-secondary text-xs py-1 px-3">📋 Copy</button>
                </div>
                <div className="p-4 max-h-[500px] overflow-y-auto">
                  <pre className="text-xs font-mono text-slate-700 whitespace-pre-wrap">{JSON.stringify(result,null,2)}</pre>
                </div>
              </div>
            )}
            {!result&&!loading&&<div className="card p-8 text-center"><span className="text-5xl block mb-3">🔧</span><p className="text-slate-500">Click the button to load capability statement</p></div>}
          </div>
        </div>
      )}
    </Layout>
  );
}
